import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Bell, Check, CheckCheck, Trash2, Search, AlertTriangle, 
  MessageSquare, PawPrint, X, Phone, Mail, User, Wifi, WifiOff 
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

// WebSocket connection for real-time notifications
const useNotificationWebSocket = (userId, onNewNotification) => {
  const wsRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    const connect = () => {
      try {
        // Get the WebSocket URL from the backend URL
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = backendUrl.replace(/^https?:/, wsProtocol);
        
        wsRef.current = new WebSocket(`${wsUrl}/api/ws/notifications/${userId}`);
        
        wsRef.current.onopen = () => {
          console.log('WebSocket connected');
          setIsConnected(true);
        };
        
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_notification') {
              onNewNotification(data.notification);
              
              // Show browser notification if permission granted
              if ('Notification' in window && Notification.permission === 'granted') {
                const notif = data.notification;
                new Notification(notif.title || 'New Notification', {
                  body: notif.message || '',
                  icon: '/favicon.ico',
                  tag: notif.notification_id
                });
              }
            }
          } catch (e) {
            console.error('Failed to parse WebSocket message:', e);
          }
        };
        
        wsRef.current.onclose = () => {
          console.log('WebSocket disconnected');
          setIsConnected(false);
          // Attempt to reconnect after 5 seconds
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        };
        
        wsRef.current.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
      }
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [userId, onNewNotification]);

  return { isConnected };
};

// Notification Icon based on type
const getNotificationIcon = (type) => {
  switch (type) {
    case 'microchip_search_alert':
      return <Search className="w-4 h-4 text-blue-500" />;
    case 'lost_pet_alert':
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    case 'message':
      return <MessageSquare className="w-4 h-4 text-green-500" />;
    default:
      return <Bell className="w-4 h-4 text-stone-500" />;
  }
};

// Notification Bell Component (for header)
export const NotificationBell = ({ onViewAll }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get('/users/notifications'),
        axios.get('/users/notifications/unread-count')
      ]);
      setNotifications(notifRes.data.slice(0, 5));
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadNotifications = async () => {
      try {
        const [notifRes, countRes] = await Promise.all([
          axios.get('/users/notifications'),
          axios.get('/users/notifications/unread-count')
        ]);
        if (isMounted) {
          setNotifications(notifRes.data.slice(0, 5));
          setUnreadCount(countRes.data.count);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    
    loadNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`/users/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put('/users/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-semibold">Notifications</span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs h-7">
              <CheckCheck className="w-3 h-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-72">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-stone-500">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem 
                key={notification.notification_id}
                className={`p-3 cursor-pointer ${!notification.read ? 'bg-blue-50' : ''}`}
                onClick={() => !notification.read && markAsRead(notification.notification_id)}
              >
                <div className="flex gap-3 w-full">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{notification.title}</p>
                    <p className="text-xs text-stone-500 truncate">{notification.message}</p>
                    <p className="text-xs text-stone-400 mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          className="justify-center text-primary cursor-pointer"
          onClick={() => { setOpen(false); onViewAll?.(); }}
        >
          View all notifications
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Full Notifications Panel (for dashboard)
export const NotificationsPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, search_alerts

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users/notifications');
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`/users/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      toast.error('Failed to update notification');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`/users/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'search_alerts') return n.type === 'microchip_search_alert';
    return true;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="mt-2 text-stone-500">Loading notifications...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant={filter === 'all' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setFilter('all')}
            >
              All
            </Button>
            <Button 
              variant={filter === 'unread' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setFilter('unread')}
            >
              Unread
            </Button>
            <Button 
              variant={filter === 'search_alerts' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setFilter('search_alerts')}
            >
              <Search className="w-3 h-3 mr-1" /> Search Alerts
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="w-12 h-12 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500">No notifications</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <div 
                key={notification.notification_id}
                className={`p-4 rounded-lg border ${!notification.read ? 'bg-blue-50 border-blue-200' : 'bg-stone-50'}`}
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold">{notification.title}</p>
                        <p className="text-sm text-stone-600 mt-1">{notification.message}</p>
                      </div>
                      <div className="flex gap-1">
                        {!notification.read && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => markAsRead(notification.notification_id)}
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => deleteNotification(notification.notification_id)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Searcher Contact Info for search alerts */}
                    {notification.type === 'microchip_search_alert' && notification.data && (
                      <div className="mt-3 p-3 bg-white rounded border">
                        <p className="text-xs font-semibold text-stone-500 mb-2">SEARCHER CONTACT INFO</p>
                        {notification.data.searcher_name && (
                          <p className="text-sm flex items-center gap-2">
                            <User className="w-3 h-3" /> {notification.data.searcher_name}
                          </p>
                        )}
                        {notification.data.searcher_email && (
                          <p className="text-sm flex items-center gap-2">
                            <Mail className="w-3 h-3" /> 
                            <a href={`mailto:${notification.data.searcher_email}`} className="text-blue-600 hover:underline">
                              {notification.data.searcher_email}
                            </a>
                          </p>
                        )}
                        {notification.data.searcher_phone && (
                          <p className="text-sm flex items-center gap-2">
                            <Phone className="w-3 h-3" /> 
                            <a href={`tel:${notification.data.searcher_phone}`} className="text-blue-600 hover:underline">
                              {notification.data.searcher_phone}
                            </a>
                          </p>
                        )}
                        {notification.data.searcher_message && (
                          <p className="text-sm mt-2 italic text-stone-600">
                            &quot;{notification.data.searcher_message}&quot;
                          </p>
                        )}
                        {!notification.data.searcher_name && !notification.data.searcher_email && !notification.data.searcher_phone && (
                          <p className="text-sm text-stone-500 italic">No contact info provided</p>
                        )}
                      </div>
                    )}
                    
                    <p className="text-xs text-stone-400 mt-2">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationBell;
